// Centralized coupon utilities for cross-page coupon management

export const ALL_PRODUCT_TYPES = [
  'base', 'pro', 'plus', 
  'spotlight', 'social_boost',
  'job_posting_single', 'job_posting_pack_3', 'job_posting_pack_5'
] as const;

export type ProductType = typeof ALL_PRODUCT_TYPES[number];

export const PRODUCT_NAMES: Record<string, string> = {
  base: 'Base',
  pro: 'Pro',
  plus: 'Plus',
  spotlight: 'Spotlight',
  social_boost: 'Social Boost',
  job_posting_single: '1 Credito Annuncio',
  job_posting_pack_3: 'Pack 3 Crediti',
  job_posting_pack_5: 'Pack 5 Crediti',
};

export interface CouponResult {
  valid: boolean;
  discountPercent: number;
  discountType: 'percentage' | 'fixed' | 'full';
  discountValue: number;
  finalPrice: number;
  message?: string;
  couponDescription?: string;
}

export interface BatchCouponResults {
  batchResults: Record<string, CouponResult | null>;
  applicablePlans: string[] | null;
  discountType: 'percentage' | 'fixed' | 'full';
  discountValue: number;
}

/**
 * Validate coupon against ALL product types globally using a single batch call
 * Returns results for all products where coupon is valid
 */
export async function validateCouponGlobally(
  supabase: any,
  code: string
): Promise<Record<string, CouponResult | null>> {
  try {
    const { data, error } = await supabase.functions.invoke('validate-coupon', {
      body: { code: code.toUpperCase(), planTypes: [...ALL_PRODUCT_TYPES] }
    });
    
    if (error) throw error;
    
    const results: Record<string, CouponResult | null> = {};
    const batchResults = data?.batchResults || {};
    
    for (const planType of ALL_PRODUCT_TYPES) {
      const r = batchResults[planType];
      if (r?.valid) {
        results[planType] = {
          valid: true,
          discountPercent: r.discountPercent || 0,
          discountType: r.discountType || 'percentage',
          discountValue: r.discountValue || r.discountPercent || 0,
          finalPrice: r.finalPrice || 0,
          couponDescription: r.couponDescription,
        };
      } else {
        results[planType] = null;
      }
    }
    
    return results;
  } catch (error) {
    console.error('Error validating coupon globally:', error);
    return {};
  }
}

/**
 * Get comma-separated list of product names where coupon is valid
 */
export function getApplicableProductNames(
  appliedCoupons: Record<string, CouponResult | null>
): string {
  return Object.keys(appliedCoupons)
    .filter(k => appliedCoupons[k]?.valid)
    .map(k => PRODUCT_NAMES[k] || k)
    .join(', ');
}

/**
 * Format discount message based on type and value
 * Returns string like "100%", "€10", "15%"
 */
export function formatDiscountMessage(
  discountType: 'percentage' | 'fixed' | 'full',
  discountValue: number
): string {
  if (discountType === 'full') {
    return '100%';
  } else if (discountType === 'fixed') {
    return `€${discountValue}`;
  } else {
    return `${discountValue}%`;
  }
}

/**
 * Save coupon data to sessionStorage for cross-page sync
 */
export function saveCouponData(code: string, results: Record<string, CouponResult | null>) {
  sessionStorage.setItem('pendingCoupon', code.toUpperCase());
  
  // Merge with existing data
  const existingData = sessionStorage.getItem('appliedCouponData');
  let mergedData = existingData ? JSON.parse(existingData) : {};
  mergedData = { ...mergedData, ...results };
  sessionStorage.setItem('appliedCouponData', JSON.stringify(mergedData));
}

/**
 * Get coupon data from sessionStorage
 */
export function getCouponData(): { code: string | null; data: Record<string, CouponResult | null> | null } {
  const code = sessionStorage.getItem('pendingCoupon');
  const data = sessionStorage.getItem('appliedCouponData');
  return { 
    code, 
    data: data ? JSON.parse(data) : null 
  };
}

/**
 * Clear all coupon data from sessionStorage
 */
export function clearCouponData() {
  sessionStorage.removeItem('pendingCoupon');
  sessionStorage.removeItem('appliedCouponData');
}

/**
 * Check if coupon has any valid applications
 */
export function hasValidCoupons(appliedCoupons: Record<string, CouponResult | null>): boolean {
  return Object.values(appliedCoupons).some(c => c?.valid);
}

/**
 * Get first valid coupon result (for extracting discount info)
 */
export function getFirstValidCoupon(appliedCoupons: Record<string, CouponResult | null>): CouponResult | null {
  return Object.values(appliedCoupons).find(c => c?.valid) || null;
}

/**
 * Format product list with "e" for last item instead of comma
 * Example: ["A", "B", "C"] -> "A, B e C"
 */
export function formatProductList(productNames: string[], locale: string = 'it'): string {
  if (productNames.length === 0) return '';
  if (productNames.length === 1) return productNames[0];
  
  // Use appropriate conjunction based on locale
  const conjunction = locale === 'es' ? ' y ' : locale === 'en' ? ' and ' : ' e ';
  
  if (productNames.length === 2) {
    return `${productNames[0]}${conjunction}${productNames[1]}`;
  }
  
  // For 3+ products: "A, B e C"
  const allButLast = productNames.slice(0, -1);
  const lastProduct = productNames[productNames.length - 1];
  return `${allButLast.join(', ')}${conjunction}${lastProduct}`;
}

/**
 * Get formatted product list from applied coupons
 */
export function getFormattedProductList(
  appliedCoupons: Record<string, CouponResult | null>,
  locale: string = 'it'
): string {
  const productNames = Object.keys(appliedCoupons)
    .filter(k => appliedCoupons[k]?.valid)
    .map(k => PRODUCT_NAMES[k] || k);
  return formatProductList(productNames, locale);
}
